<!-- 
Dialog for editing fees for a levy collection.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Edit fees & rates</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="fees-and-rates"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-2>
            <v-layout wrap>
              <v-flex xs12 sm6>
                <our-currency-input
                  v-if="show"
                  :value="configuration.administrationFeePerProvider" 
                  :autofocus="!state.isNew" 
                  required 
                  zero-or-greater
                  max="999999"
                  label="Administrative fee per provider*" class="levy-collection-administrationFeePerProvider"
                  @input="update('administrationFeePerProvider', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-currency-input 
                  :value="configuration.administrationFeePerStudent" required zero-or-greater
                  label="Administrative fee per student*" class="levy-collection-administrationFeePerStudent"
                  @input="update('administrationFeePerStudent', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-currency-input 
                  :value="configuration.riskPremiumPerStudent" required zero-or-greater
                  label="Risk premium per student*" class="levy-collection-riskPremiumPerStudent"
                  @input="update('riskPremiumPerStudent', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-percentage-input 
                  :value="configuration.riskPremiumRate" :decimal-places="3" required zero-or-greater
                  label="Risk premium percentage*" class="levy-collection-riskPremiumRate"
                  @input="update('riskPremiumRate', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-percentage-input
                  :value="configuration.specialTuitionProtectionRate" :decimal-places="3" required zero-or-greater
                  label="Special tuition protection percentage*" suffix="%" class="levy-collection-specialTuitionProtectionRate"
                  @input="update('specialTuitionProtectionRate', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-layout>
                  <v-flex shrink>
                    <v-checkbox 
                      :value="configuration.enableLevyCap" 
                      hide-details
                      color="primary"
                      @change="update('enableLevyCap', !!$event)"
                    />
                  </v-flex>
                  <v-flex grow>
                    <our-percentage-input
                      :disabled="!configuration.enableLevyCap" 
                      :value="configuration.levyCap" :decimal-places="3"
                      :label="(configuration.enableLevyCap ? 'Levy cap*' : 'Levy cap')"
                      :required="configuration.enableLevyCap"
                      :greater-than-zero="configuration.enableLevyCap"
                      :unrestricted-range="configuration.enableLevyCap"
                      suffix="%" class="levy-collection-levyCap"
                      @input="update('levyCap', $event)" 
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="success" 
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import moment from "moment";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false,
      year: value => (!value || parseInt(value) >= this.currentYear ? true : `Must be ${this.currentYear} or later`)
    };
  },
  computed: {
    currentYear() {
      return new moment().year();
    },
    state() {
      return this.$store.state.levyCollections.editFees;
    },
    configuration() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.dispatch(action.EDIT_FEES_GET, this.levyCollectionId);
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_FEES_SET_DATA, { [field]: newValue });
      if (field == "enableLevyCap") this.$refs.form.resetValidation();
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_FEES_SAVE))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
